import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { XMarkIcon, CameraIcon, PhotoIcon, StopIcon } from '@heroicons/react/24/outline';

interface ScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onScan: (decodedText: string) => void;
}

export const ScannerModal: React.FC<ScannerModalProps> = ({ isOpen, onClose, onScan }) => {
    const [isScanning, setIsScanning] = useState(false);
    const [cameraId, setCameraId] = useState<string | null>(null);
    const [cameras, setCameras] = useState<Array<{ id: string; label: string }>>([]);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;

        // Initialize scanner
        const initScanner = () => {
            try {
                if (!scannerRef.current) {
                    scannerRef.current = new Html5Qrcode("reader-custom", {
                        verbose: false,
                        formatsToSupport: [
                            Html5QrcodeSupportedFormats.QR_CODE,
                            Html5QrcodeSupportedFormats.CODE_128,
                        ]
                    });
                }
            } catch (e) {
                console.error("Failed to initialize Html5Qrcode", e);
            }
        };

        // Delay slightly to ensure DOM is ready
        const timer = setTimeout(initScanner, 100);

        return () => {
            mountedRef.current = false;
            clearTimeout(timer);

            // Cleanup on unmount
            if (scannerRef.current) {
                const scanner = scannerRef.current;
                try {
                    // Critical fix: react-html5-qrcode crashes if we call clear() when the DOM is removed.
                    // We only need to stop the camera stream.
                    if (scanner.isScanning) {
                        scanner.stop().catch(err => console.warn("Scanner stop error on unmount", err));
                    }
                } catch (e) {
                    console.warn("Cleanup error", e);
                }
            }
        };
    }, []);

    const startScanning = async () => {
        if (!scannerRef.current) return;

        try {
            const devices = await Html5Qrcode.getCameras();
            if (!mountedRef.current) return;

            if (devices && devices.length) {
                setCameras(devices);
                const camId = cameraId || devices[0].id;
                setCameraId(camId);

                // Double check if already scanning to avoid error
                if (scannerRef.current.isScanning) {
                    await scannerRef.current.stop();
                }

                await scannerRef.current.start(
                    camId,
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 },
                        aspectRatio: 1.0,
                    },
                    (decodedText) => {
                        if (mountedRef.current) {
                            onScan(decodedText);
                            handleClose();
                        }
                    },
                    (_errorMessage) => {
                        // ignore
                    }
                );

                if (mountedRef.current) {
                    setIsScanning(true);
                }
            } else {
                alert("No se encontraron cámaras.");
            }
        } catch (err: any) {
            console.error("Error starting scanner", err);
            // Ignore "scan is ongoing" errors which might happen in race conditions
            if (err?.toString().includes("scan is ongoing")) {
                setIsScanning(true);
            } else {
                alert("Error al acceder a la cámara o iniciar el escáner.");
            }
        }
    };

    const stopScanning = async () => {
        if (scannerRef.current && isScanning) {
            try {
                await scannerRef.current.stop();
                if (mountedRef.current) {
                    setIsScanning(false);
                }
            } catch (err) {
                console.error("Failed to stop", err);
            }
        }
    };

    const handleClose = async () => {
        // Stop scanning before notifying parent to close (which unmounts us)
        if (scannerRef.current && scannerRef.current.isScanning) {
            try {
                await scannerRef.current.stop();
            } catch (e) {
                console.log("Stop before close error", e);
            }
        }
        onClose();
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0 && scannerRef.current) {
            const file = e.target.files[0];
            scannerRef.current.scanFile(file, true)
                .then(decodedText => {
                    onScan(decodedText);
                    handleClose();
                })
                .catch(err => {
                    console.error("Error scanning file", err);
                    alert("No se pudo leer el código QR de la imagen.");
                });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100] animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative border border-gray-100" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-xl font-bold text-gray-800">Escanear Código</h3>
                    <button
                        onClick={handleClose}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 flex flex-col items-center gap-6">
                    {/* Viewport Area */}
                    <div className="relative w-full overflow-hidden rounded-xl bg-black h-64">
                        {/* Dedicated container for the library. React should NEVER touch children of this. */}
                        <div id="reader-custom" className="w-full h-full"></div>

                        {/* Overlay UI - completely separate from the reader container */}
                        {!isScanning && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-gray-400 pointer-events-none">
                                <CameraIcon className="w-16 h-16 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Cámara inactiva</p>
                            </div>
                        )}
                    </div>

                    {/* Camera Selection (if multiple) */}
                    {cameras.length > 1 && isScanning && (
                        <select
                            value={cameraId || ''}
                            onChange={(e) => {
                                stopScanning().then(() => {
                                    setCameraId(e.target.value);
                                });
                            }}
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        >
                            {cameras.map(cam => (
                                <option key={cam.id} value={cam.id}>{cam.label || `Cámara ${cam.id.slice(0, 5)}...`}</option>
                            ))}
                        </select>
                    )}

                    {/* Controls */}
                    <div className="flex flex-col w-full gap-3">
                        {!isScanning ? (
                            <button
                                onClick={startScanning}
                                className="w-full py-3.5 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2"
                            >
                                <CameraIcon className="w-5 h-5" />
                                Iniciar Cámara
                            </button>
                        ) : (
                            <button
                                onClick={stopScanning}
                                className="w-full py-3.5 px-6 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold shadow-lg shadow-red-200 transition-all flex items-center justify-center gap-2"
                            >
                                <StopIcon className="w-5 h-5" />
                                Detener Cámara
                            </button>
                        )}

                        <div className="relative">
                            <input
                                type="file"
                                id="qr-image-upload"
                                accept="image/*"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-3.5 px-6 bg-white border-2 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-600 text-gray-600 rounded-xl font-medium transition-all flex items-center justify-center gap-2 group"
                            >
                                <PhotoIcon className="w-5 h-5 group-hover:text-blue-500 transition-colors" />
                                Subir Imagen QR
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-400 text-center">
                        Apunta la cámara al código QR o sube una imagen para leerlo.
                    </p>
                </div>
            </div>
        </div>
    );
};
